<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { PopoverClose } from 'reka-ui'
import {
  AlertCircleIcon,
  ArrowUpRightIcon,
  Link2Icon,
  Link2OffIcon,
  RefreshCwIcon,
  XIcon,
} from '@lucide/vue'

import { LinkedDataAccount, LinkedDataConnectorStatus } from '@types'
import { ROUTE_NAME } from '@/const'

import { stringToLocalDateString } from '@/helpers/dates'

import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'

import {
  DSAvatar,
  DSAvatarFallback,
  DSButton,
  DSEmpty,
  DSEmptyDescription,
  DSEmptyMedia,
  DSPopover,
  DSPopoverContent,
  DSPopoverTrigger,
  DSSkeleton,
  DSToast,
} from '@ds'
import { cn } from '@ds/lib/utils'

import { AssetTreeConnectorInfo } from './utils/types'

type Props = {
  assetId: string
  connectorInfo: AssetTreeConnectorInfo
  getLinkedAccountInfo?: (key: string) => Promise<LinkedDataAccount> | undefined
}

const { assetId, connectorInfo, getLinkedAccountInfo } = defineProps<Props>()

const router = useRouter()
const { resync, syncing } = useLinkedDataConnector()

const isOpen = ref(false)
const isAccountInfoLoading = ref(false)
const accountInfo = ref<LinkedDataAccount | undefined>()

const connectorInitial = computed(() => accountInfo.value?.connector_name?.split('').slice(0, 2).join('').toUpperCase() || '?')
const lastSyncedLabel = computed(() => stringToLocalDateString(accountInfo.value?.account_last_updated, { relative: true }))
const isAccountConnected = computed(() => accountInfo.value?.last_update_status === 'SUCCESS')
const accountStatusLabel = computed(() => isAccountConnected.value ? 'Connected' : accountInfo.value?.last_update_status)

const getAccountInfo = async () => {
  try {
    isAccountInfoLoading.value = true
    accountInfo.value = await getLinkedAccountInfo?.(assetId)
  } catch (error) {
    DSToast.error(error as string)
    accountInfo.value = undefined
  } finally {
    isAccountInfoLoading.value = false
  }
}

// Only the closed→open transition should fetch — the trigger's own click also
// fires when it's clicked again to close, and clicking outside/Escape/the X
// close it without ever touching the trigger, so neither should re-fetch.
watch(isOpen, open => {
  if (open) getAccountInfo()
})

const handleOpenAccount = () => {
  if (!accountInfo.value) return
  isOpen.value = false
  router.push({
    name: ROUTE_NAME.LINKED_DATA_ACCOUNT,
    params: {
      connector_id: accountInfo.value.connector_id,
      account_id: accountInfo.value.id,
    },
  })
}

const handleSync = () => {
  if (!accountInfo.value) return
  resync(accountInfo.value.connector_id)
}
</script>

<template>
  <DSPopover v-model:open="isOpen">
    <DSPopoverTrigger
      as-child
      @click.stop
    >
      <DSButton
        type="button"
        variant="ghost"
        size="icon-sm"
        :title="connectorInfo.message"
        :class="cn(
          'size-5.5 shrink-0 text-primary hover:bg-primary/10',
          connectorInfo.status === LinkedDataConnectorStatus.WARNING && 'text-warning hover:bg-warning/10',
          (connectorInfo.status === LinkedDataConnectorStatus.ERROR
            || connectorInfo.status === LinkedDataConnectorStatus.LOGIN_REQUIRED)
            && 'text-destructive hover:bg-destructive/10',
        )"
      >
        <component
          :is="connectorInfo.status === LinkedDataConnectorStatus.ERROR
            || connectorInfo.status === LinkedDataConnectorStatus.LOGIN_REQUIRED ? Link2OffIcon : Link2Icon"
          aria-hidden="true"
        />
      </DSButton>
    </DSPopoverTrigger>
    <DSPopoverContent
      align="start"
      class="w-72 p-0 overflow-hidden"
    >
      <div
        v-if="isAccountInfoLoading && !accountInfo"
        class="p-3 flex flex-col gap-3"
      >
        <div class="flex items-center gap-2.5">
          <DSSkeleton class="size-8 rounded-ds-md shrink-0" />
          <div class="flex-1 flex flex-col gap-1.5">
            <DSSkeleton class="h-3.5 w-28" />
            <DSSkeleton class="h-3 w-20" />
          </div>
        </div>
        <DSSkeleton class="h-16 w-full" />
      </div>
      <DSEmpty
        v-if="!accountInfo"
        class="gap-2 p-4!"
      >
        <DSEmptyMedia class="mb-0">
          <AlertCircleIcon
            class="size-5 text-warning"
            aria-hidden="true"
          />
        </DSEmptyMedia>
        <DSEmptyDescription>
          Couldn't load account details.
        </DSEmptyDescription>
      </DSEmpty>
      <template v-else>
        <div class="flex items-start gap-2.5 p-3">
          <DSAvatar
            shape="square"
            size="xs"
            class="bg-primary/10 text-primary"
          >
            <DSAvatarFallback>{{ connectorInitial }}</DSAvatarFallback>
          </DSAvatar>
          <div class="flex-1 min-w-0 pt-0.5">
            <p class="text-sm font-bold text-foreground truncate">
              {{ accountInfo.account_name }}
            </p>
            <p class="text-xs font-medium text-muted-foreground">
              Linked via {{ accountInfo.connector_name }}
            </p>
          </div>
          <PopoverClose
            class="shrink-0 size-6 -mt-0.5 -mr-0.5 rounded-ds-sm flex items-center justify-center text-muted-foreground hover:bg-background hover:text-foreground cursor-pointer"
          >
            <XIcon
              class="size-3.5"
              aria-hidden="true"
            />
          </PopoverClose>
        </div>
        <div class="border-t border-border px-3 py-2.5 grid grid-cols-2 gap-y-1.5 text-sm">
          <span class="text-muted-foreground">Account</span>
          <span class="text-right font-medium text-foreground tabular-nums">{{ accountInfo.account_number ?? '—' }}</span>
          <span class="text-muted-foreground">Last synced</span>
          <span class="text-right font-medium text-foreground">{{ lastSyncedLabel }}</span>
          <span class="text-muted-foreground">Status</span>
          <span
            class="text-right font-medium flex items-center justify-end gap-1.5"
            :class="isAccountConnected ? 'text-success' : 'text-muted-foreground'"
          >
            <span class="size-1.5 rounded-full shrink-0 bg-current" />
            {{ accountStatusLabel }}
          </span>
        </div>
        <div class="border-t border-border p-2.5 flex gap-2">
          <DSButton
            type="button"
            variant="primary"
            size="sm"
            class="flex-1"
            @click="handleOpenAccount"
          >
            Open account
            <ArrowUpRightIcon aria-hidden="true" />
          </DSButton>
          <DSButton
            type="button"
            variant="secondary"
            size="sm"
            :disabled="syncing"
            @click="handleSync"
          >
            <RefreshCwIcon
              :class="syncing && 'animate-spin'"
              aria-hidden="true"
            />
            Sync
          </DSButton>
        </div>
      </template>
    </DSPopoverContent>
  </DSPopover>
</template>
